/* eslint-disable @typescript-eslint/camelcase */
import {
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import Loading from 'components/Loading';
import i18n from 'i18n';
import { messagesRequest } from 'modules/messages/actions';
import { getAdminPinRequest } from 'modules/environments/actions';
import { ClientFields } from 'modules/clients/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import formStyles from '../Form/styles';
import envFormSecurityStyles from '../../Environments/Configuration/FormSecurity/styles';

const useFormStyles = makeStyles(formStyles);
const useSecurityStyles = makeStyles(envFormSecurityStyles);

interface Props {
  client: ClientFields | null;
  environmentUuid: string | undefined;
  isLoadingAdminPin: boolean;
  adminPin: ApplicationState['environments']['adminPin'];
  getAdminPinRequest: (p: { environmentUuid: string }) => void;
  messagesRequest: (props: { type: string; message: string }) => void;
}

const ClientSecureDeviceValidation = ({
  environmentUuid,
  isLoadingAdminPin,
  adminPin,
  getAdminPinRequest,
  messagesRequest,
}: Props) => {
  const classes = useFormStyles();
  const secClasses = useSecurityStyles();
  const [showPin, setShowPin] = React.useState(false);

  React.useEffect(() => {
    if (environmentUuid) {
      getAdminPinRequest({ environmentUuid });
    }
  }, [environmentUuid, getAdminPinRequest]);

  const pin = adminPin?.environment_admin_pin ?? '';

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid container>
        <Typography className={classes.sectionTitle} variant="h4">
          {i18n.t('SECURE_DEVICE_VALIDATION_KEY')}
        </Typography>
      </Grid>

      {isLoadingAdminPin && !pin ? (
        <Grid item xs={12}>
          <Loading />
        </Grid>
      ) : (
        <Grid
          container
          spacing={2}
          alignItems="center"
          className={secClasses.currentPinGrid}
        >
          <Grid item xs={12} sm={6} md={4} className={classes.currentPin}>
            <TextField
              label={i18n.t('ADMIN_PIN_KEY')}
              value={pin}
              fullWidth
              variant="outlined"
              type={showPin ? 'text' : 'password'}
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: {
                  fontSize: 28,
                  letterSpacing: '0.5em',
                  textAlign: 'center',
                },
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle pin visibility"
                      onClick={() => setShowPin(prev => !prev)}
                      edge="end"
                    >
                      {showPin ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled
            />
          </Grid>
          <Grid item container xs={12} sm={6} spacing={1} alignItems="center">
            <Grid item>
              <Tooltip arrow title={i18n.t('COPY_PIN_KEY')}>
                <span>
                  <IconButton
                    aria-label="copy"
                    disabled={!pin}
                    onClick={() => {
                      navigator.clipboard.writeText(pin);
                      messagesRequest({
                        type: 'success',
                        message: i18n.t('PIN_COPIED_KEY'),
                      });
                    }}
                  >
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const mapStateToProps = (
  { environments: { isLoadingAdminPin, adminPin } }: ApplicationState,
  { client }: { client: ClientFields | null }
) => ({
  isLoadingAdminPin,
  adminPin,
  environmentUuid: client?.environment?.environment_uuid,
});

export default connect(mapStateToProps, {
  getAdminPinRequest,
  messagesRequest,
})(ClientSecureDeviceValidation);
